import { Jobs } from '../JobManager';

const API_URL = '/api/products';

class ProductsService {
    async getAll() {
        var response = await fetch(API_URL, {
            headers: this.getHeaders()
        });
        if (!response.ok) throw new Error('Failed to fetch products');
        return await response.json();
    }

    async getById(id) {
        var response = await fetch(API_URL + '/' + id, {
            headers: this.getHeaders()
        });
        if (!response.ok) throw new Error('Failed to fetch product');
        return await response.json();
    }

    async getByName(productName) {
        var response = await fetch(API_URL + '/byname/' + productName, {
            headers: this.getHeaders()
        });
        if (!response.ok) throw new Error('Failed to fetch product');
        return await response.json();
    }

    async create(productData) {
        var response = await fetch(API_URL, {
            method: 'POST',
            headers: this.getHeaders(),
            body: JSON.stringify(productData)
        });
        if (!response.ok) {
            var error = await response.json();
            throw new Error(error.message || 'Failed to create product');
        }
        return await response.json();
    }

    async update(id, productData) {
        var response = await fetch(API_URL + '/' + id, {
            method: 'PUT',
            headers: this.getHeaders(),
            body: JSON.stringify(productData)
        });
        if (!response.ok) {
            var error = await response.json();
            throw new Error(error.message || 'Failed to update product');
        }
        return await response.json();
    }

    async delete(id) {
        var response = await fetch(API_URL + '/' + id, {
            method: 'DELETE',
            headers: this.getHeaders()
        });
        if (!response.ok) throw new Error('Failed to delete product');
        return await response.json();
    }

    async addParameter(productId, paramData) {
        var response = await fetch(API_URL + '/' + productId + '/parameters', {
            method: 'POST',
            headers: this.getHeaders(),
            body: JSON.stringify(paramData)
        });
        if (!response.ok) throw new Error('Failed to add parameter');
        return await response.json();
    }

    async updateParameter(parameterId, paramData) {
        var response = await fetch(API_URL + '/parameters/' + parameterId, {
            method: 'PUT',
            headers: this.getHeaders(),
            body: JSON.stringify(paramData)
        });
        if (!response.ok) throw new Error('Failed to update parameter');
        return await response.json();
    }

    async deleteParameter(parameterId) {
        var response = await fetch(API_URL + '/parameters/' + parameterId, {
            method: 'DELETE',
            headers: this.getHeaders()
        });
        if (!response.ok) throw new Error('Failed to delete parameter');
        return await response.json();
    }

    // Upload ZIP file and create product — returns packageId for SignalR adopt job
    async uploadProduct(file, productData, onProgress) {
        var formData = new FormData();
        formData.append('package', file);
        formData.append('displayName', productData.displayName || '');
        formData.append('description', productData.description || '');
        formData.append('rootFileName', productData.rootFileName || '');
        formData.append('drawingFilePath', productData.drawingFilePath || '');
        formData.append('inventorVersion', productData.inventorVersion || 'Autodesk.Inventor+2026');

        var token = localStorage.getItem('token');

        return new Promise(function(resolve, reject) {
            var xhr = new XMLHttpRequest();

            xhr.upload.addEventListener('progress', function(e) {
                if (e.lengthComputable && onProgress) {
                    var percentComplete = Math.round((e.loaded / e.total) * 100);
                    onProgress(percentComplete);
                }
            });

            xhr.addEventListener('load', function() {
                if (xhr.status >= 200 && xhr.status < 300) {
                    try {
                        var response = JSON.parse(xhr.responseText);
                        resolve(response);
                    } catch (err) {
                        resolve({ message: 'Upload successful' });
                    }
                } else {
                    try {
                        var error = JSON.parse(xhr.responseText);
                        reject(new Error(error.message || 'Upload failed'));
                    } catch (err) {
                        reject(new Error('Upload failed with status ' + xhr.status));
                    }
                }
            });

            xhr.addEventListener('error', function() {
                reject(new Error('Network error during upload'));
            });

            xhr.addEventListener('abort', function() {
                reject(new Error('Upload cancelled'));
            });

            xhr.open('POST', API_URL + '/upload');
            if (token) {
                xhr.setRequestHeader('Authorization', 'Bearer ' + token);
            }
            xhr.send(formData);
        });
    }

    // Trigger APS Design Automation adopt job via SignalR
    // This uploads to OSS bucket, runs DA to extract parameters, create viewable, BOM, etc.
    adoptProduct(packageId, onStart, onComplete, onError) {
        var jobManager = Jobs();
        jobManager.doAdoptJob(
            packageId,
            onStart,
            function(newProject, stats, reportUrl) {
                // onComplete — DA processing finished successfully
                if (onComplete) {
                    onComplete(newProject, stats, reportUrl);
                }
            },
            function(errorData) {
                // onError — DA processing failed
                if (onError) {
                    onError(errorData);
                }
            }
        );
    }

    // Analyze ZIP file to find assembly files
    async analyzeZip(file) {
        var formData = new FormData();
        formData.append('package', file);

        var response = await fetch(API_URL + '/analyze-zip', {
            method: 'POST',
            headers: this.getAuthHeader(),
            body: formData
        });

        if (!response.ok) {
            var error = await response.json();
            throw new Error(error.message || 'Failed to analyze ZIP file');
        }
        return await response.json();
    }

    getHeaders() {
        var token = localStorage.getItem('token');
        return {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': 'Bearer ' + token })
        };
    }

    getAuthHeader() {
        var token = localStorage.getItem('token');
        return token ? { 'Authorization': 'Bearer ' + token } : {};
    }
}

export default new ProductsService();